import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, CheckCircle2 } from 'lucide-react';

const certifications = [
  {
    name: "GOTS",
    description: "Global Organic Textile Standard certified for organic garment production.",
    logo: "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/im7i4e8g_Lu-VK3MS5FRtOLCABouoE.avif",
    category: "Organic",
  },
  {
    name: "Oeko-Tex Standard 100",
    description: "Certified by AITEX for harmful substance-free textiles safe for human health.",
    logo: "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/wqyzo0j1_B00mtE_LxhbKn4JDBVVmc.avif",
    category: "Safety",
  },
  {
    name: "SMETA",
    description: "Sedex Members Ethical Trade Audit for social and ethical compliance verification.",
    logo: "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/5ihyrbpt_qGJ9uJ2qm1Eh6yGtfTKgi.avif",
    category: "Audit",
  },
  {
    name: "BCI",
    description: "Better Cotton Initiative member promoting sustainable cotton farming practices.",
    logo: "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/ez1dxage_U1FJzRbgWMgzN_aVUl6Jk.avif",
    category: "Sustainable",
  },
  {
    name: "Higg Index",
    description: "Registered on Higg Index for environmental and social sustainability assessment.",
    logo: "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/tfsxp9mv_5WXaPQ6Wf2O60LZzJEB5I.avif",
    category: "Environmental",
  },
  {
    name: "SEDEX",
    description: "Supplier Ethical Data Exchange member for responsible supply chain management.",
    icon: ShieldCheck,
    category: "Ethical",
  },
  {
    name: "ICS",
    description: "Initiative for Compliance and Sustainability certification for ethical manufacturing.",
    icon: CheckCircle2,
    category: "Compliance",
  },
  {
    name: "NEXT Approved",
    description: "Approved manufacturer for NEXT UK meeting their rigorous quality standards.",
    icon: CheckCircle2,
    category: "Brand",
  },
];

const complianceAreas = [
  "Health & Safety Compliance",
  "Social Compliance",
  "Physical Security",
  "Environmental Compliance",
  "Child Labor Free",
  "Fair Wages Policy",
];

const CertificationsSection = () => {
  return (
    <section 
      id="certifications"
      data-testid="certifications-section"
      className="py-24 md:py-32 bg-slate-50"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <p className="font-sans text-sm font-medium tracking-[0.3em] uppercase text-slate-400 mb-4">
            Certifications & Compliance
          </p>
          <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-slate-900 mb-6">
            Quality & Ethics<br />
            <span className="text-rose-700">You Can Trust</span>
          </h2>
          <p className="font-sans text-lg text-slate-600 max-w-2xl mx-auto">
            Our facilities are audited and approved by leading international certification bodies, 
            ensuring compliance with global standards for quality, safety, and ethical manufacturing.
          </p>
        </motion.div>

        {/* Certifications Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {certifications.map((cert, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="cert-card bg-white p-8 border border-slate-100 group"
              data-testid={`cert-card-${index}`}
            >
              {/* Category Badge */}
              <span className="inline-block font-sans text-xs font-medium tracking-wider uppercase text-rose-700 bg-rose-50 px-3 py-1 mb-4">
                {cert.category}
              </span>
              
              {/* Logo or Icon */}
              {cert.logo ? (
                <div className="w-full h-20 flex items-center justify-start mb-4">
                  <img 
                    src={cert.logo} 
                    alt={`${cert.name} certification`}
                    className="h-16 w-auto object-contain"
                  />
                </div>
              ) : (
                <div className="w-14 h-14 bg-slate-900 flex items-center justify-center mb-4 group-hover:bg-rose-700 transition-colors duration-300">
                  <cert.icon size={28} className="text-white" strokeWidth={1.5} />
                </div>
              )}
              
              {/* Content */}
              <h3 className="font-serif text-xl text-slate-900 mb-2">
                {cert.name}
              </h3>
              <p className="font-sans text-sm text-slate-600 leading-relaxed">
                {cert.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Compliance Areas */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-slate-900 p-8 md:p-12"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-8">
            <div className="md:w-1/3">
              <p className="font-sans text-xs font-medium tracking-[0.2em] uppercase text-white/50 mb-2">
                Full Compliance
              </p>
              <h3 className="font-serif text-2xl md:text-3xl text-white mb-4">
                Independent Audits Available
              </h3>
              <p className="font-sans text-sm text-white/70">
                All prospective customers can have independent audits done for their reference. 
                We maintain complete transparency throughout our supply chain.
              </p>
            </div>
            <div className="md:w-2/3 grid grid-cols-2 md:grid-cols-3 gap-4">
              {complianceAreas.map((area, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-4 bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <CheckCircle2 size={16} className="text-rose-400 flex-shrink-0" strokeWidth={2} />
                  <span className="font-sans text-sm text-white">{area}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CertificationsSection;
